import type { TFunction } from 'i18next';
import { formatDateTime, formatLanguage, formatList } from '../../lib/format';
import type { AssistantAction } from './assistant-api';

const asText = (value: unknown): string => (typeof value === 'string' || typeof value === 'number' ? String(value) : '');

/** The display values of a summary: dates, languages and lists through the shared formatter, never by hand. */
function summaryParams(t: TFunction, params: Record<string, unknown>): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) out[key] = formatList(value.map((field) => t(`assistant.field.${asText(field)}`, { defaultValue: asText(field) })));
    else if (typeof value === 'number') out[key] = value;
    else out[key] = asText(value);
  }
  for (const key of ['scheduled_at', 'previous_scheduled_at'] as const) {
    const iso = asText(params[key]);
    if (iso) out[key] = formatDateTime(iso);
  }
  const language = asText(params.language);
  if (language) out.language = formatLanguage(language);
  return out;
}

/** One line for an action, from the server's `summary_key` and params; an unknown key still reads as a sentence. */
export function actionSummary(t: TFunction, action: AssistantAction): string {
  const known = action.summary_key.startsWith('assistant.action.') ? action.summary_key : 'assistant.action.other';
  const key = t(known, { defaultValue: '' }) === '' ? 'assistant.action.other' : known;
  return t(key, summaryParams(t, action.summary_params));
}

/** Where Oeffnen leads: the object the action changed (a comment, localization or variant leads to its post). */
export function actionTarget(action: AssistantAction): string | null {
  const itemId = asText(action.summary_params.content_item_id) || action.target_id;
  switch (action.target_type) {
    case 'content_item':
    case 'localization':
    case 'variant':
    case 'comment':
      return `/content/${itemId}`;
    case 'campaign':
      return `/campaigns/${action.target_id}`;
    case 'publication':
      return `/publications/${action.target_id}`;
    case 'publication_group':
      return '/publications';
    default:
      return null;
  }
}
